"use client"

import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ArrowLeft, Trash2, Calendar, MessageSquare, Target, Volume2, Share } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { ResponseCard } from "@/components/response-card"
import { createClientSupabaseClient } from "@/supabase/client"
import { useLanguage } from "@/contexts/language-context"
import { useAuth } from "@/contexts/auth-context"
import { parseResponseContent } from "@/lib/response-parser"
import { useCommunicationOptions } from "@/hooks/use-communication-options"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

interface HistoryItem {
  id: string
  input_content: string
  description?: string | null
  scenario: string | null
  target: string | null
  tone: string | null
  response_content: string
  public: boolean
  created_at: string
}

interface PaginationInfo {
  page: number
  limit: number
  total: number
  totalPages: number
  hasMore: boolean
}

interface HistoryModalProps {
  isOpen: boolean
  onClose: () => void
}

export function HistoryModal({ isOpen, onClose }: HistoryModalProps) {
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([])
  const [filteredItems, setFilteredItems] = useState<HistoryItem[]>([])
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null)
  const [sortBy, setSortBy] = useState<string>("newest")
  const [filterScenario, setFilterScenario] = useState<string>("all")
  const [filterTarget, setFilterTarget] = useState<string>("all")
  const [filterTone, setFilterTone] = useState<string>("all")
  const [isLoading, setIsLoading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showShareConfirm, setShowShareConfirm] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const [isSavingTemplate, setIsSavingTemplate] = useState(false)
  const [templateSaved, setTemplateSaved] = useState(false)
  const [isSharing, setIsSharing] = useState(false)

  // Pagination related states
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [allItemsLoaded, setAllItemsLoaded] = useState(false)

  // Add retry control states - use useRef to ensure immediate effect
  const isRetryingRef = useRef(false)
  const lastRetryTimeRef = useRef(0)
  const authFailedRef = useRef(false) // Use ref to ensure auth failure state takes effect immediately
  const [isRetrying, setIsRetrying] = useState(false)
  const [authFailed, setAuthFailed] = useState(false) // Track authentication failure state

  const { toast } = useToast()
  const { t } = useLanguage()
  const { user, isAuthenticated, userSubscription } = useAuth() // Use authentication context
  const { scenarios: allScenarios, targets: allTargets, tones: allTones, getScenarioLabel, getTargetLabel, getToneLabel } = useCommunicationOptions()

  const isFree = !isAuthenticated || userSubscription?.plan === 'free'

  // Ensure Portal is only rendered on client side
  useEffect(() => {
    setIsClient(true)
  }, [])



  // Helper function to get session with timeout and retry
  const getSessionWithTimeout = async (timeoutMs: number = 2000): Promise<{ session: any; hasAccessToken: boolean }> => {
    return new Promise(async (resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('getSession timeout'))
      }, timeoutMs)

      try {
        const supabase = createClientSupabaseClient()
        // console.log('📱 Supabase client created successfully')

        const { data: { session } } = await supabase.auth.getSession()
        clearTimeout(timeoutId)

        const hasAccessToken = !!(session?.access_token)
        // console.log(`Session status: {session: ${!!session}, hasAccessToken: ${hasAccessToken}}`)

        resolve({ session, hasAccessToken })
      } catch (error) {
        clearTimeout(timeoutId)
        reject(error)
      }
    })
  }

  const fetchHistory = async (loadMore: boolean = false, retryCount: number = 0) => {
    // Prevent infinite retry - skip if currently retrying or too soon since last retry
    const now = Date.now()
    // console.log(`🔍 fetchHistory called: loadMore=${loadMore}, retryCount=${retryCount}, isRetrying=${isRetryingRef.current}, timeSinceLastRetry=${now - lastRetryTimeRef.current}ms`)

    if (isRetryingRef.current || (now - lastRetryTimeRef.current < 5000 && retryCount === 0)) {
      // console.log('🚫 Skip duplicate request to prevent infinite retry', { isRetrying: isRetryingRef.current, timeSinceLastRetry: now - lastRetryTimeRef.current, retryCount })
      return
    }

    if (retryCount === 0) {
      // console.log('🔄 Setting retry state')
      isRetryingRef.current = true
      lastRetryTimeRef.current = now
      setIsRetrying(true)
    }

    if (loadMore) {
      setIsLoadingMore(true)
    } else {
      setIsLoading(true)
      setHistoryItems([])
      setFilteredItems([])
      setPagination(null)
      setAllItemsLoaded(false)
    }

    try {
      // Check authentication status
      // console.log('🔑 Checking authentication status...', { user: !!user, isAuthenticated })

      if (!user || !isAuthenticated) {
        throw new Error('No authentication - user not logged in')
      }

      // Use cookie authentication, skip session retrieval
      // console.log('🔑 Using cookie authentication, skipping session retrieval...')

      // console.log('🚀 Starting to fetch history data...')

      // Build query parameters
      const params = new URLSearchParams({
        page: loadMore ? String((pagination?.page || 0) + 1) : '1',
        limit: '10'
      })

      if (sortBy !== 'newest') params.append('sortBy', sortBy)
      if (filterScenario !== 'all') params.append('filterScenario', filterScenario)
      if (filterTarget !== 'all') params.append('filterTarget', filterTarget)
      if (filterTone !== 'all') params.append('filterTone', filterTone)

      // Set request timeout
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 15000) // 15 second timeout

      try {
        // console.log('🔑 Sending request using cookie authentication')

        const response = await fetch(`/api/history-cookie?${params}`, {
          credentials: 'include', // Ensure cookies are included
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('AUTH_ERROR')
          } else if (response.status >= 500) {
            throw new Error('CONNECTION_ERROR')
          }
          throw new Error(`HTTP ${response.status}`)
        }

        const data = await response.json()
        // console.log('📊 History data fetched successfully:', data)

        if (loadMore) {
          setHistoryItems(prev => [...prev, ...(data.history || [])])
        } else {
          setHistoryItems(data.history || [])
        }

        if (data.pagination) {
          setPagination(data.pagination)
          setAllItemsLoaded(!data.pagination.hasMore)
        }

        // Reset retry count and auth failure state after success
        if (retryCount > 0) {
          toast({
            title: "Connection Restored",
            description: "History loaded successfully!",
            variant: "default",
          })
        }

        // Reset authentication failure state
        if (authFailed) {
          setAuthFailed(false)
        }

      } catch (fetchError) {
        clearTimeout(timeoutId)
        throw fetchError
      }

    } catch (error) {
      console.error('Error fetching history:', error)

      const errorMessage = error instanceof Error ? error.message : 'Unknown error'

      // Only auto-retry for network or database timeout, don't auto-retry for auth errors
      const shouldRetry = (
        errorMessage === 'TIMEOUT' ||
        errorMessage === 'CONNECTION_ERROR' ||
        errorMessage.includes('timeout') ||
        errorMessage.includes('aborted') ||
        errorMessage.includes('connection') ||
        errorMessage.includes('network')
      )

      if (shouldRetry && retryCount < 3) {
        // Auto retry with increasing delay
        const delay = Math.min(2000 * Math.pow(2, retryCount), 8000) // 2s, 4s, 8s

        toast({
          title: retryCount === 0 ? "Connecting to Database" : "Retrying",
          description: `Connecting to database, auto retry in ${delay / 1000} seconds... (${retryCount + 1}/3)`,
          variant: "default",
        })

        setTimeout(() => {
          fetchHistory(loadMore, retryCount + 1)
        }, delay)
        return
      } else if (shouldRetry) {
        // Final failure, provide manual retry
        toast({
          title: "Connection Failed",
          description: "Multiple retries failed, please retry manually or refresh the page.",
          variant: "destructive",
          action: (
            <button
              onClick={() => {
                isRetryingRef.current = false
                lastRetryTimeRef.current = 0
                setIsRetrying(false)
                fetchHistory(loadMore, 0)
              }}
              className="px-3 py-1 bg-white text-red-600 rounded text-sm hover:bg-gray-100"
            >
              Retry
            </button>
          )
        })
      }
      // Handle authentication errors - don't auto-retry, stop directly
      else if (errorMessage === 'AUTH_ERROR' ||
        errorMessage === 'No authentication token' ||
        errorMessage === 'No authentication - user not logged in' ||
        errorMessage === 'Authentication failed' ||
        errorMessage.includes('Unauthorized')) {
        // console.log('🛑 Authentication failed, stopping all retries')
        isRetryingRef.current = false
        authFailedRef.current = true // Immediately set auth failure state to prevent useEffect duplicate triggers

        setAuthFailed(true) // Set authentication failure state
        setIsRetrying(false)

        toast({
          title: "Authentication Failed",
          description: "Please refresh the page to log in again, or try again later.",
          variant: "destructive",
          action: (
            <button
              onClick={() => {
                // Reset all states to allow user retry
                authFailedRef.current = false
                setAuthFailed(false)
                isRetryingRef.current = false
                lastRetryTimeRef.current = 0
                setIsRetrying(false)
                onClose() // Close window
              }}
              className="px-3 py-1 bg-white text-red-600 rounded text-sm hover:bg-gray-100"
            >
              Close Window
            </button>
          )
        })
        return // Return directly, don't continue execution
      }
      // Other errors
      else {
        toast({
          title: "Loading Failed",
          description: "Unable to load history, please check network connection and retry.",
          variant: "destructive",
          action: (
            <button
              onClick={() => {
                isRetryingRef.current = false
                lastRetryTimeRef.current = 0
                setIsRetrying(false)
                fetchHistory(loadMore, 0)
              }}
              className="px-3 py-1 bg-white text-red-600 rounded text-sm hover:bg-gray-100"
            >
              Retry
            </button>
          )
        })
      }
    } finally {
      if (loadMore) {
        setIsLoadingMore(false)
      } else {
        setIsLoading(false)
      }

      // Reset retry state
      if (retryCount === 0) {
        isRetryingRef.current = false
        setIsRetrying(false)
      }
    }
  }

  // Load more data
  const loadMoreHistory = async () => {
    if (isLoadingMore || allItemsLoaded) return
    await fetchHistory(true)
  }

  // Scroll detection function
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget

    // Load more when scrolling near bottom (trigger when 50px from bottom)
    if (scrollHeight - scrollTop - clientHeight < 50 && !isLoadingMore && !allItemsLoaded) {
      loadMoreHistory()
    }
  }

  // Delete history item
  const deleteHistoryItem = async (id: string) => {
    setIsDeleting(true)
    try {
      // Get auth token from Supabase
      const supabase = createClientSupabaseClient()
      const { data: { session } } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('No authentication token')
      }

      const response = await fetch(`/api/history/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to delete history item')
      }

      // Remove from local state
      setHistoryItems(prev => prev.filter(item => item.id !== id))

      // Close confirmation dialog
      setShowDeleteConfirm(false)

      // Return to history message list page (clear selected item)
      setSelectedItem(null)

      toast({
        title: t('status.success'),
        description: t('history.deleteSuccess'),
      })
    } catch (error) {
      console.error('Error deleting history item:', error)
      toast({
        title: t('status.error'),
        description: t('history.deleteFailed'),
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const saveAsTemplate = async () => {
    if (!selectedItem) return
    setIsSavingTemplate(true)
    try {
      // Use description as template content, use "No description" if null
      const templateContent = selectedItem.description || "No description"
      const res = await fetch('/api/templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: `Template ${new Date().toLocaleString()}`,
          scenario: selectedItem.scenario,
          target: selectedItem.target,
          tone: selectedItem.tone,
          content: templateContent
        })
      })
      if (!res.ok) throw new Error('Failed')
      toast({ title: t('templates.saved'), description: t('templates.saveSuccess') })
      setTemplateSaved(true)
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('templates-changed'))
      }
    } catch (err) {
      console.error(err)
      toast({ title: t('status.error'), description: t('templates.saveFailed'), variant: 'destructive' })
    } finally {
      setIsSavingTemplate(false)
    }
  }

  const shareToShowcase = async () => {
    if (!selectedItem) return
    setIsSharing(true)
    try {
      const response = await fetch(`/api/history/${selectedItem.id}/share`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          public: true
        })
      })

      if (!response.ok) {
        throw new Error('Failed to share to showcase')
      }

      // Update local state
      setHistoryItems(prev => prev.map(item =>
        item.id === selectedItem.id ? { ...item, public: true } : item
      ))

      // Update selected item
      setSelectedItem(prev => prev ? { ...prev, public: true } : null)

      // Close confirmation dialog
      setShowShareConfirm(false)

      toast({
        title: t('status.success'),
        description: t('history.shareSuccess'),
      })
    } catch (error) {
      console.error('Error sharing to showcase:', error)
      toast({
        title: t('status.error'),
        description: t('history.shareFailed'),
        variant: "destructive",
      })
    } finally {
      setIsSharing(false)
    }
  }

  // Filter and sort items
  useEffect(() => {
    // Reload data when filter or sort conditions change
    // But avoid triggering new requests during retry process or after auth failure
    // And don't trigger on initial open (handled by modal open useEffect)
    if (isOpen && !isRetryingRef.current && !authFailedRef.current && historyItems.length > 0) {
      fetchHistory(false)
    }
  }, [sortBy, filterScenario, filterTarget, filterTone])

  // Apply frontend filtering and sorting (for loaded data)
  useEffect(() => {
    if (!Array.isArray(historyItems)) return

    let filtered = [...historyItems]

    // Apply filters
    if (filterScenario !== "all") {
      filtered = filtered.filter(item => item.scenario === filterScenario)
    }
    if (filterTarget !== "all") {
      filtered = filtered.filter(item => item.target === filterTarget)
    }
    if (filterTone !== "all") {
      filtered = filtered.filter(item => item.tone === filterTone)
    }

    // Apply sorting
    switch (sortBy) {
      case "newest":
        filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        break
      case "oldest":
        filtered.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
        break
      case "scenario":
        filtered.sort((a, b) => (a.scenario || '').localeCompare(b.scenario || ''))
        break
      case "target":
        filtered.sort((a, b) => (a.target || '').localeCompare(b.target || ''))
        break
      case "tone":
        filtered.sort((a, b) => (a.tone || '').localeCompare(b.tone || ''))
        break
    }

    setFilteredItems(filtered)
  }, [historyItems])

  // Fetch history when modal opens
  useEffect(() => {
    if (isOpen) {
      authFailedRef.current = false // Reset auth failure state ref
      setAuthFailed(false) // Reset auth failure state
      fetchHistory()
      setSelectedItem(null)
    }
  }, [isOpen])

  useEffect(() => {
    setIsSavingTemplate(false)
    setTemplateSaved(false)
  }, [selectedItem, isOpen])

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDeleteConfirm) {
        setShowDeleteConfirm(false)
      }
    }

    if (showDeleteConfirm) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [showDeleteConfirm])



  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Truncate text for card preview
  const truncateText = (text: string, maxLength: number = 100) => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  // Test functions
  const handleCancelClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    // console.log('Cancel clicked - handler function')
    // alert('Cancel button works!')
    // Only close confirmation window, stay on details page
    setShowDeleteConfirm(false)
  }

  const handleDeleteClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    // console.log('Delete clicked - handler function')
    // alert('Delete button works!')
    // Execute delete operation
    deleteHistoryItem(selectedItem?.id || '')
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent
          onInteractOutside={
            showDeleteConfirm || showShareConfirm
              ? (e) => e.preventDefault()
              : undefined
          }
          className={`max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-gray-color ${showDeleteConfirm || showShareConfirm ? 'pointer-events-none' : ''
            }`}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItem(null)}
                    className="p-1 bg-white text-black hover:bg-gray-100"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  {t('history.messageDetails')}
                </>
              ) : (
                <>
                  <MessageSquare className="w-5 h-5" />
                  {t('history.listTitle')}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedItem
                ? t('history.messageDetailsDesc')
                : t('history.listDesc')
              }
            </DialogDescription>
          </DialogHeader>

          {selectedItem ? (
            // Detail View
            <div className="flex-1 overflow-y-auto space-y-6">
              {/* Message Info */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mr-4">
                <Card>
                  <CardContent className="p-4 bg-card-color">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <Calendar className="w-4 h-4" />
                      {t('history.field.date')}
                    </div>
                    <div className="font-medium">{formatDate(selectedItem.created_at)}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 bg-card-color">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <MessageSquare className="w-4 h-4" />
                      {t('history.field.scenario')}
                    </div>
                    <div className="font-medium">{getScenarioLabel(selectedItem.scenario || 'none')}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 bg-card-color">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <Target className="w-4 h-4" />
                      {t('history.field.target')}
                    </div>
                    <div className="font-medium">{getTargetLabel(selectedItem.target || 'none')}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 bg-card-color">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-1">
                      <Volume2 className="w-4 h-4" />
                      {t('history.field.tone')}
                    </div>
                    <div className="font-medium">{getToneLabel(selectedItem.tone || 'none')}</div>
                  </CardContent>
                </Card>
              </div>

              {/* Original Input */}
              <Card>
                <CardContent className="p-6 bg-zinc-color">
                  <h3 className="text-lg font-semibold mb-3">{t('history.originalMessage')}</h3>
                  <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg p-4 bg-card-color">
                    <p className="whitespace-pre-wrap">{selectedItem.input_content}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Description (if exists) */}
              {selectedItem.description && (
                <Card>
                  <CardContent className="p-6 bg-zinc-color">
                    <h3 className="text-lg font-semibold mb-3">{t('history.description')}</h3>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-500">
                      <p className="whitespace-pre-wrap text-blue-800 dark:text-blue-200">{selectedItem.description}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* AI Response */}
              <div>
                <h3 className="text-lg font-semibold mb-3">{t('history.aiResponse')}</h3>
                {(() => {
                  const { response, translation } = parseResponseContent(selectedItem.response_content)
                  return (
                    <ResponseCard
                      response={response}
                      translation={translation}
                      isStreaming={false}
                    />
                  )
                })()}
              </div>

              {/* Actions */}
              <div className="pt-4 border-t">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Button
                    onClick={() => setShowShareConfirm(true)}
                    disabled={isSharing || selectedItem?.public}
                    className={`flex items-center gap-2 ${selectedItem?.public
                      ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                      }`}
                  >
                    {isSharing ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : selectedItem?.public ? (
                      t("history.showcased")
                    ) : (
                      t("history.shareToShowcase")
                    )}
                  </Button>

                  <div className="flex flex-wrap gap-2 ml-auto">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={saveAsTemplate}
                            disabled={isSavingTemplate || templateSaved || isFree}
                            className="flex items-center gap-2 bg-botton-color"
                          >
                            {isSavingTemplate ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : templateSaved ? (
                              t('templates.saved')
                            ) : (
                              t('templates.saveAsTemplate')
                            )}
                          </Button>
                        </TooltipTrigger>
                        {/* {isFree && (
                          <TooltipContent>Only for subscribed users(Pro/Max)</TooltipContent>
                        )} */}
                      </Tooltip>
                    </TooltipProvider>

                    <Button
                      variant="destructive"
                      onClick={() => setShowDeleteConfirm(true)}
                      disabled={isDeleting}
                      className="flex items-center gap-2 bg-botton-color"
                    >
                      <Trash2 className="w-4 h-4" />
                      {isDeleting ? t('history.deleting') : t('history.deleteMessage')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // List View
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Filters and Sorting */}
              <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 dark:bg-zinc-800 rounded-lg">
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('history.sortBy')}</label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="newest">{t('history.sort.newest')}</SelectItem>
                      <SelectItem value="oldest">{t('history.sort.oldest')}</SelectItem>
                      <SelectItem value="scenario">{t('history.sort.scenario')}</SelectItem>
                      <SelectItem value="target">{t('history.sort.target')}</SelectItem>
                      <SelectItem value="tone">{t('history.sort.tone')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('history.filter.scenario')}</label>
                  <Select value={filterScenario} onValueChange={setFilterScenario}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">{t('history.filter.all')}</SelectItem>
                      {allScenarios.map(scenario => (
                        <SelectItem key={scenario.id} value={scenario.id}>
                          {scenario.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('history.filter.target')}</label>
                  <Select value={filterTarget} onValueChange={setFilterTarget}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">{t('history.filter.all')}</SelectItem>
                      {allTargets.map(target => (
                        <SelectItem key={target.id} value={target.id}>
                          {target.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium">{t('history.filter.tone')}</label>
                  <Select value={filterTone} onValueChange={setFilterTone}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">{t('history.filter.all')}</SelectItem>
                      {allTones.map(tone => (
                        <SelectItem key={tone.id} value={tone.id}>
                          {tone.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* History Cards Grid */}
              <div className="flex-1 overflow-y-auto" onScroll={handleScroll}>
                {isLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">{t('history.loading')}</div>
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-gray-500">{t('history.none')}</div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {filteredItems.map((item) => (
                        <Card
                          key={item.id}
                          className="hover-glow-card cursor-pointer bg-card-color transition-shadow"
                          onClick={() => setSelectedItem(item)}
                        >
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              {/* Date */}
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(item.created_at)}
                              </div>

                              {/* Preview Text */}
                              <div className="text-sm">
                                <p className="line-clamp-3">{truncateText(item.input_content)}</p>
                              </div>

                              {/* Tags */}
                              <div className="flex flex-wrap gap-1">
                                {item.scenario && (
                                  <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                                    {getScenarioLabel(item.scenario || 'none')}
                                  </span>
                                )}
                                {item.target && (
                                  <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                                    {getTargetLabel(item.target || 'none')}
                                  </span>
                                )}
                                {item.tone && (
                                  <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                                    {getToneLabel(item.tone || 'none')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Load more indicator */}
                    {isLoadingMore && (
                      <div className="flex items-center justify-center py-4">
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                          {t('history.loadingMore')}
                        </div>
                      </div>
                    )}

                    {/* Pagination info */}
                    {!isLoading && filteredItems.length > 0 && pagination && (
                      <div className="flex items-center justify-between py-4 text-sm text-gray-500 border-t">
                        <div>
                          {t('history.showing').replace('{current}', String(filteredItems.length)).replace('{total}', String(pagination.total))}
                        </div>
                        {allItemsLoaded && pagination.total > pagination.limit && (
                          <div className="text-xs">
                            {t('history.allLoaded')}
                          </div>
                        )}
                        {!allItemsLoaded && pagination.hasMore && (
                          <Button
                            onClick={loadMoreHistory}
                            disabled={isLoadingMore}
                            variant="outline"
                            size="sm"
                            className="text-xs px-3 py-1 h-auto"
                          >
                            {isLoadingMore ? (
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin"></div>
                                {t('history.loadingMore')}
                              </div>
                            ) : (
                              t('history.loadMoreButton')
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog - render to body using Portal */}
      {isClient && showDeleteConfirm && selectedItem && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center"
          style={{
            zIndex: 1000, // Maximum z-index value
            pointerEvents: 'auto'
          }}
          onClick={() => {
            setShowDeleteConfirm(false)
          }}
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-lg p-6 max-w-md mx-4 shadow-xl"
            style={{ pointerEvents: 'auto' }}
            onClick={() => {
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('history.confirmDeleteTitle')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('history.confirmDeleteSubtitle')}</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                {t('history.confirmDeleteDesc')}
              </p>
              <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {selectedItem.input_content}
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleCancelClick}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                style={{ pointerEvents: 'auto' }}
              >
                {t('common.back')}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  handleDeleteClick(e)
                }}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 flex items-center gap-2"
                style={{ pointerEvents: 'auto' }}
              >
                {isDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('history.deleting')}
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    {t('history.confirmDeleteAction')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Share confirmation dialog - render to body using Portal */}
      {isClient && showShareConfirm && selectedItem && createPortal(
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center"
          style={{
            zIndex: 1000,
            pointerEvents: 'auto'
          }}
          onClick={() => {
            setShowShareConfirm(false)
          }}
        >
          <div
            className="bg-white dark:bg-zinc-900 rounded-lg p-6 max-w-md mx-4 shadow-xl"
            style={{ pointerEvents: 'auto' }}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Share className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('history.shareTitle')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('history.shareSubtitle')}</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-3">
                {t("history.shareConfirm")}
              </p>
              <div className="bg-gray-50 dark:bg-zinc-800 rounded-lg p-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {selectedItem.input_content}
                </p>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowShareConfirm(false)}
                disabled={isSharing}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                style={{ pointerEvents: 'auto' }}
              >
                {t('common.back')}
              </button>
              <button
                type="button"
                onClick={shareToShowcase}
                disabled={isSharing}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
                style={{ pointerEvents: 'auto' }}
              >
                {isSharing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('history.sharing')}
                  </>
                ) : (
                  <>
                    <Share className="w-4 h-4" />
                    {t('history.confirmShareAction')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}